import { ApplicationConfig, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { PreloadAllModules, provideRouter, TitleStrategy, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { environment } from 'environments/environment';
import { AlertModule } from '@full-fledged/alerts';
import { API_BASE_URL } from './shared/services/service-shared';
import { APP_VERSION } from './shared/components/iapply-version/iapply-version.component';
import { popperVariation, provideTippyConfig, tooltipVariation } from '@ngneat/helipopper';
import { TemplateProviderService } from './core/template-provider/template-provider.service';
import { provideNgxMask } from 'ngx-mask';
import { provideUserConfig } from '@iapply/iapply.provider';
import { TemplatePageTitleStrategy } from './app-title-strategy';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(
            withInterceptorsFromDi(),
        ),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Custom iApply browser title strategy
        { provide: TitleStrategy, useClass: TemplatePageTitleStrategy },

        // iApply user Configuration service.
        provideUserConfig(),

        {
            provide: APP_BASE_HREF,
            useFactory: (plat: PlatformLocation) => plat.getBaseHrefFromDOM(),
            deps: [PlatformLocation]
        },

        // iApply Template Provider
        provideAppInitializer(() => {
            const initializerFn = ((templateProviderService: TemplateProviderService) => () => templateProviderService.loadCustomControls())(inject(TemplateProviderService));
            return initializerFn();
        }),

        // iApply Server configuration provider
        // provideAppInitializer(() => {
        //     const initializerFn = ((configService: SiteConfigurationService) => () => configService.load())(inject(SiteConfigurationService));
        //     return initializerFn();
        // });

        // Tooltips...
        provideTippyConfig({
            defaultVariation: 'tooltip',
            allowHTML: true,
            variations: {
                tooltip: tooltipVariation,
                popper: popperVariation,
            }
        }),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'enterprise',
                scheme: 'light', // light, dark, auto
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),

        // provideServiceWorker('ngsw-worker.js'); // Included in sw.bundle
        // provideServiceWorker('sw.bundle.js', {
        //     enabled: true, // !isDevMode(),
        //     registrationStrategy: 'registerImmediately', // 'registerWhenStable:30000' (default),
        //     // scope: '/'
        // }),

        importProvidersFrom(
            AlertModule.forRoot({ maxMessages: 5, timeout: 5000, positionX: 'right' }),
        ),

        { provide: API_BASE_URL, useFactory: (): string => environment.config.remoteServiceBaseUrl },
        { provide: APP_VERSION, useFactory: (): string => environment.version },

        // ROLLBAR
        // { provide: ErrorHandler, useClass: RollbarErrorHandler },
        // { provide: RollbarService, useFactory: rollbarFactory },
        // { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },

        // Ngx-Mask
        provideNgxMask(),
    ],
};
